'use client';

import React, { Suspense, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { CheckCircle } from 'lucide-react';
import { createClient, getAuthRedirectUrl } from '@/utils/supabase/client';

function getEmailProviderUrl(email: string): { name: string; url: string } | null {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;
  if (domain === 'gmail.com' || domain === 'googlemail.com') return { name: 'Gmail', url: 'https://mail.google.com' };
  if (domain === 'outlook.com' || domain === 'hotmail.com' || domain === 'live.com') return { name: 'Outlook', url: 'https://outlook.live.com' };
  if (domain === 'yahoo.com' || domain === 'ymail.com') return { name: 'Yahoo Mail', url: 'https://mail.yahoo.com' };
  if (domain === 'protonmail.com' || domain === 'proton.me' || domain === 'pm.me') return { name: 'Proton Mail', url: 'https://mail.proton.me' };
  if (domain === 'icloud.com' || domain === 'me.com' || domain === 'mac.com') return { name: 'iCloud Mail', url: 'https://www.icloud.com/mail' };
  if (domain === 'zoho.com') return { name: 'Zoho Mail', url: 'https://mail.zoho.com' };
  if (domain === 'rediffmail.com') return { name: 'Rediffmail', url: 'https://mail.rediff.com' };
  return null;
}

function AuthErrorContent() {
  const searchParams = useSearchParams();
  const errorType = (searchParams.get('error') || 'unknown').toLowerCase();
  const rawMessage = searchParams.get('message');

  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  // Prefill email from query param or localStorage
  useEffect(() => {
    const qEmail = searchParams.get('email');
    if (qEmail) {
      setResendEmail(qEmail);
    } else if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('kheldrop_last_signup_email');
      if (stored) setResendEmail(stored);
    }
  }, [searchParams]);

  const isOtpExpired = errorType === 'otp_expired' || errorType === 'expired' || rawMessage?.toLowerCase().includes('expired');

  const errorDetails: Record<string, { title: string; description: string }> = {
    otp_expired: {
      title: 'Verification Link Expired or Already Used',
      description:
        'Security links expire quickly for your protection. Often, email security scanners (such as Outlook, Defender, or antivirus filters) pre-check the link before you click it, which consumes the single-use token. Request a fresh link below.',
    },
    expired: {
      title: 'Verification Link Expired',
      description:
        'This verification or password reset link has expired. Security links are valid for a limited time. Request a new link below to continue.',
    },
    invalid: {
      title: 'Invalid Authentication Link',
      description:
        'This link is malformed or has already been used to confirm your account. If you already verified, try signing in.',
    },
    access_denied: {
      title: 'Email Link Expired or Denied',
      description:
        rawMessage ||
        'The verification token was expired or denied by the authentication server. You can request a fresh verification link below.',
    },
    unknown: {
      title: 'Authentication Error',
      description:
        rawMessage || 'An unexpected authentication error occurred while verifying your link. Please try again.',
    },
  };

  const current = errorDetails[errorType] || (isOtpExpired ? errorDetails.otp_expired : errorDetails.unknown);

  async function handleResend(e: React.FormEvent) {
    e.preventDefault();
    if (!resendEmail) {
      setResendError('Please enter your email address.');
      return;
    }

    setResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      const supabase = createClient();
      const emailRedirectTo = getAuthRedirectUrl('/auth/callback');

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: resendEmail.trim().toLowerCase(),
        options: { emailRedirectTo },
      });

      if (error) {
        setResendError(error.message || 'Failed to resend link. Please try again.');
      } else {
        setResendSuccess(true);
        if (typeof window !== 'undefined') {
          localStorage.setItem('kheldrop_last_signup_email', resendEmail.trim().toLowerCase());
        }
      }
    } catch (err: any) {
      setResendError(err.message || 'Failed to resend link. Please try again.');
    } finally {
      setResending(false);
    }
  }

  const provider = resendEmail ? getEmailProviderUrl(resendEmail) : null;

  return (
    <div style={{ textAlign: 'center' }}>
      <div
        className="auth-success-icon"
        style={{
          background: isOtpExpired ? 'rgba(245, 158, 11, 0.14)' : 'rgba(239, 68, 68, 0.12)',
          color: isOtpExpired ? '#d97706' : 'var(--danger, #ef4444)',
        }}
      >
        {isOtpExpired ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        )}
      </div>

      <h2 className="auth-success-title">{current.title}</h2>
      <p className="auth-success-text" style={{ marginBottom: '24px' }}>
        {current.description}
      </p>

      {/* Resend Verification Form if OTP or access issue */}
      {(isOtpExpired || errorType === 'access_denied') && (
        <div
          style={{
            background: 'var(--elevated, #f4f4f6)',
            border: '1px solid var(--border, #e8e8ed)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>
            Request a fresh verification link
          </div>

          {resendSuccess ? (
            <div
              style={{
                background: 'rgba(22, 163, 74, 0.12)',
                border: '1px solid #16a34a',
                color: '#15803d',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '13px',
                marginBottom: '12px',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle size={14} style={{ flexShrink: 0 }} /> Fresh verification email sent!
              </div>
              <div>Please check your inbox (and spam folder) for the new link.</div>
            </div>
          ) : (
            <form onSubmit={handleResend} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <input
                className="auth-input"
                type="email"
                placeholder="Enter your account email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                required
                disabled={resending}
                style={{ width: '100%', fontSize: '13px', padding: '10px 12px' }}
              />

              {resendError && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '2px' }}>
                  {resendError}
                </div>
              )}

              <button
                type="submit"
                className="auth-submit"
                disabled={resending}
                style={{ width: '100%', marginTop: '4px', padding: '10px' }}
              >
                {resending ? (
                  <>
                    <span className="auth-spinner" />
                    Sending new link…
                  </>
                ) : (
                  'Resend Verification Email'
                )}
              </button>
            </form>
          )}

          {provider && (
            <a
              href={provider.url}
              target="_blank"
              rel="noopener noreferrer"
              className="auth-oauth-btn"
              style={{
                marginTop: '10px',
                width: '100%',
                textDecoration: 'none',
                display: 'flex',
                fontSize: '13px',
                padding: '9px',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Open {provider.name}
            </a>
          )}
        </div>
      )}

      {/* Alternative actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Link href="/login" className="auth-submit" style={{ textDecoration: 'none', display: 'flex' }}>
          Back to Sign In
        </Link>
        <Link href="/forgot-password" className="auth-oauth-btn" style={{ textDecoration: 'none', display: 'flex' }}>
          Reset Password Instead
        </Link>
      </div>

      <div className="auth-footer" style={{ marginTop: '24px' }}>
        Need a fresh start?{' '}
        <Link href="/signup">Create a new account</Link>
      </div>
    </div>
  );
}

export default function AuthErrorPage() {
  return (
    <Suspense fallback={<div className="auth-spinner" style={{ margin: '40px auto', display: 'block' }} />}>
      <AuthErrorContent />
    </Suspense>
  );
}
